"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.hasBackgroundUploadFailedOnStage = hasBackgroundUploadFailedOnStage;
exports.hasBackgroundUploadOnStageEnded = hasBackgroundUploadOnStageEnded;
exports.useBackgroundUploadedOnStageSLI = void 0;
var _react = require("react");
var _useCommonPageActionsApi = require("../../client/react/useCommonPageActionsApi");
var _reactRouterDom = require("react-router-dom");
/**
 * This function checks whether background has been uploaded on stage successfully or not
 * @param {object} user - user who uploaded background
 * @param {string} prevProgramBackgroundId - Previous backgropundId on stage
 * @param {string} programBackgroundId - backgroundId of background on stage
 * @param {function} trackableUserEvent - action to track user
 */
function hasBackgroundUploadOnStageEnded(userId, prevProgramBackgroundId, programBackgroundId, trackableUserEvent) {
  return (prevProgramBackgroundId === null || prevProgramBackgroundId === void 0 ? void 0 : prevProgramBackgroundId.current) && programBackgroundId !== (prevProgramBackgroundId === null || prevProgramBackgroundId === void 0 ? void 0 : prevProgramBackgroundId.current) && (trackableUserEvent === null || trackableUserEvent === void 0 ? void 0 : trackableUserEvent.userId) === userId && (trackableUserEvent === null || trackableUserEvent === void 0 ? void 0 : trackableUserEvent.name) === "backgroundUploadStarted:".concat(programBackgroundId);
}

/**
 * Checks whether the background upload has failed on stage.
 *
 * @param {string} userId - The ID of the user who uploaded the background.
 * @param {boolean} isFailed - Indicates if the upload has failed.
 * @param {object} trackableUserEvent - The event object used to track the user action.
 * @returns {boolean} - Returns true if the background upload has failed and the event matches the user and event name.
 */
function hasBackgroundUploadFailedOnStage(userId, isFailed, trackableUserEvent) {
  return isFailed && (trackableUserEvent === null || trackableUserEvent === void 0 ? void 0 : trackableUserEvent.userId) === userId && (trackableUserEvent === null || trackableUserEvent === void 0 ? void 0 : trackableUserEvent.name.match(/backgroundUploadStarted:/));
}

/**
 * Custom hook to manage the state of background uploads to the stage.
 *
 * @returns {Object} An object containing the following properties:
 * - startBackgroundUploadedOnStageSLI: Function to start the background upload on stage.
 * - endBackgroundUploadedOnStageSLI: Function to end the background upload on stage.
 * - endBackgroundUploadedOnStageSLIIncomplete: Function to handle incomplete background uploads.
 */
var useBackgroundUploadedOnStageSLI = exports.useBackgroundUploadedOnStageSLI = function useBackgroundUploadedOnStageSLI() {
  var _useCommonPageActions = (0, _useCommonPageActionsApi.useCommonPageActionsApi)(),
    startBackgroundUploadedOnStage = _useCommonPageActions.startBackgroundUploadedOnStage,
    endBackgroundUploadedOnStage = _useCommonPageActions.endBackgroundUploadedOnStage;
  var match = (0, _reactRouterDom.useRouteMatch)();
  var prevProgramBackgroundId = (0, _react.useRef)(null);

  /**
   * Starts the background upload on stage SLI.
   *
   * @param {string} userId - The ID of the user who initiated the background upload.
   * @param {function} trackUserEvent - The action to track user events.
   * @param {function} dispatch - The Redux dispatch function.
   */
  var startBackgroundUploadedOnStageSLI = (0, _react.useCallback)(function (userId, trackUserEvent, dispatch, backgroundId) {
    startBackgroundUploadedOnStage({});
    dispatch(trackUserEvent(userId, "backgroundUploadStarted:".concat(backgroundId)));
  }, [startBackgroundUploadedOnStage]);

  /**
   * Ends the background upload on stage SLI based on the upload status and events.
   *
   * @param {string} userId - The ID of the user who uploaded the background.
   * @param {boolean} isFailed - Indicates if the upload has failed.
   * @param {string} programBackgroundId - The ID of the background on stage.
   * @param {object} trackableUserEvent - The event object used to track the user action.
   * @param {function} trackUserEvent - The action to track user events.
   * @param {function} dispatch - The Redux dispatch function.
   */
  var endBackgroundUploadedOnStageSLI = (0, _react.useCallback)(function (userId, isFailed, programBackgroundId, trackableUserEvent, trackUserEvent, dispatch, message) {
    if (hasBackgroundUploadFailedOnStage(userId, isFailed, trackableUserEvent)) {
      endBackgroundUploadedOnStage({
        error: message
      });
      dispatch(trackUserEvent(userId, 'backgroundUploadStopped'));
      return true;
    } else if (hasBackgroundUploadOnStageEnded(userId, prevProgramBackgroundId, programBackgroundId, trackableUserEvent)) {
      endBackgroundUploadedOnStage({
        pathName: match.path,
        url: match.url
      });
      dispatch(trackUserEvent(userId, 'backgroundUploadStopped'));
      prevProgramBackgroundId.current = programBackgroundId;
      return true;
    } else {
      prevProgramBackgroundId.current = programBackgroundId;
      return false;
    }
  }, [endBackgroundUploadedOnStage, match.path, match.url]);

  /**
   * Ends the background upload SLI as incomplete if a new background upload is initiated before the previous one is completed.
   *
   * @param {string} trackableUserEventUserId - The ID of the user associated with the event.
   * @param {string} trackableUserEventName - The name of the event.
   * @param {string} userId - The ID of the user who initiated the background upload.
   */
  var endBackgroundUploadedOnStageSLIIncomplete = (0, _react.useCallback)(function (trackableUserEventUserId, trackableUserEventName, userId) {
    if (trackableUserEventUserId === userId && /^backgroundUploadStarted/.test(trackableUserEventName)) {
      endBackgroundUploadedOnStage({
        incomplete_measure: 'User initiated the new background upload before the previous background upload was completed'
      });
    }
  }, [endBackgroundUploadedOnStage]);
  return {
    startBackgroundUploadedOnStageSLI: startBackgroundUploadedOnStageSLI,
    endBackgroundUploadedOnStageSLI: endBackgroundUploadedOnStageSLI,
    endBackgroundUploadedOnStageSLIIncomplete: endBackgroundUploadedOnStageSLIIncomplete
  };
};